/**
 * Connection status indicator
 * Shows offline status and pending sync count
 */

import { Wifi, WifiOff, RefreshCw, CloudOff, Check } from 'lucide-react'
import { useOnlineStatus } from '../hooks/useOnlineStatus'
import { cn } from '../lib/utils'
import { useState, useEffect } from 'react'
import { idb, STORES } from '../lib/idb'

/**
 * Format an ISO timestamp as "5 min ago", "2 hr ago", or "3 days ago".
 * Used by the offline banner to tell a user how stale their cached data is
 * (so they know whether to trust offline inventory / customer lists).
 */
function formatRelativeAge(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} hr ago`
  const days = Math.floor(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

/**
 * Hook returning the most recent IDB cache refresh time across the offline
 * stores actually used in the field (products, customers, inventory). Returns
 * null until at least one store has been populated.
 */
function useCacheAge(): string | null {
  const [age, setAge] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const refresh = async () => {
      try {
        const metas = await Promise.all([
          idb.getCacheMeta(STORES.PRODUCTS),
          idb.getCacheMeta(STORES.CUSTOMERS),
          idb.getCacheMeta(STORES.INVENTORY),
        ])
        const newest = metas
          .filter((m): m is { store: string; lastUpdated: string; count: number } => !!m)
          .map(m => m.lastUpdated)
          .sort()
          .pop()
        if (!cancelled) setAge(newest ?? null)
      } catch {
        if (!cancelled) setAge(null)
      }
    }
    refresh()
    const interval = setInterval(refresh, 60_000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [])

  return age
}

interface ConnectionStatusProps {
  className?: string
  showSyncButton?: boolean
}

export default function ConnectionStatus({
  className,
  showSyncButton = true
}: ConnectionStatusProps) {
  const { isOnline, pendingCount, isSyncing, lastSyncResult, syncNow } = useOnlineStatus()
  const [showSuccess, setShowSuccess] = useState(false)

  // Show success indicator briefly after sync
  useEffect(() => {
    if (lastSyncResult && lastSyncResult.synced > 0) {
      setShowSuccess(true)
      const timer = setTimeout(() => setShowSuccess(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [lastSyncResult])

  // Don't show anything if online with no pending items
  if (isOnline && pendingCount === 0 && !showSuccess) {
    return null
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Success indicator */}
      {showSuccess && (
        <div className="flex items-center gap-1 px-2 py-1 bg-success-500/20 text-success-100 rounded-full text-xs animate-fade-in">
          <Check size={12} />
          <span>Synced</span>
        </div>
      )}

      {/* Offline indicator */}
      {!isOnline && (
        <div className="flex items-center gap-1 px-2 py-1 bg-warning-500/20 text-warning-100 rounded-full text-xs">
          <WifiOff size={12} />
          <span>Offline</span>
        </div>
      )}

      {/* Pending count badge */}
      {pendingCount > 0 && (
        <button
          onClick={showSyncButton && isOnline ? syncNow : undefined}
          disabled={isSyncing || !isOnline}
          className={cn(
            'flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-all',
            isOnline
              ? 'bg-primary-500/20 text-primary-100 hover:bg-primary-500/30 active:scale-95'
              : 'bg-gray-500/20 text-gray-300'
          )}
        >
          {isSyncing ? (
            <RefreshCw size={12} className="animate-spin" />
          ) : (
            <CloudOff size={12} />
          )}
          <span>{pendingCount} pending</span>
        </button>
      )}
    </div>
  )
}

/**
 * Compact connection status for tight spaces
 */
export function ConnectionStatusCompact({ className }: { className?: string }) {
  const { isOnline, pendingCount, isSyncing } = useOnlineStatus()

  if (isOnline && pendingCount === 0) {
    return null
  }

  return (
    <div className={cn('flex items-center gap-1', className)}>
      {!isOnline ? (
        <WifiOff size={16} className="text-warning-400" />
      ) : isSyncing ? (
        <RefreshCw size={16} className="text-primary-300 animate-spin" />
      ) : pendingCount > 0 ? (
        <div className="relative">
          <CloudOff size={16} className="text-primary-300" />
          <span className="absolute -top-1 -right-1 bg-warning-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
            {pendingCount > 9 ? '9+' : pendingCount}
          </span>
        </div>
      ) : (
        <Wifi size={16} className="text-success-400" />
      )}
    </div>
  )
}

/**
 * Full-width offline banner
 */
export function OfflineBanner() {
  const { isOnline, pendingCount, isSyncing, syncNow } = useOnlineStatus()
  const cacheAge = useCacheAge()

  if (isOnline && pendingCount === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'px-4 py-2 flex items-center justify-between text-sm gap-3',
        !isOnline
          ? 'bg-warning-500 text-white'
          : 'bg-primary-500 text-white'
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        {!isOnline ? (
          <>
            <WifiOff size={16} className="shrink-0" />
            <div className="min-w-0">
              <p className="truncate">You're offline. Changes will sync when back online.</p>
              {cacheAge && (
                <p className="text-xs opacity-80">Data last refreshed {formatRelativeAge(cacheAge)}</p>
              )}
            </div>
          </>
        ) : (
          <>
            <CloudOff size={16} className="shrink-0" />
            <span>{pendingCount} changes pending sync</span>
          </>
        )}
      </div>
      {isOnline && pendingCount > 0 && (
        <button
          onClick={syncNow}
          disabled={isSyncing}
          className="flex items-center gap-1 px-3 py-1 bg-white/20 rounded-lg hover:bg-white/30 transition-colors shrink-0"
        >
          <RefreshCw size={14} className={cn(isSyncing && 'animate-spin')} />
          <span>{isSyncing ? 'Syncing...' : 'Sync Now'}</span>
        </button>
      )}
    </div>
  )
}
